import React from 'react';
import { motion } from 'framer-motion';

interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  icon?: React.ReactNode;
  fullWidth?: boolean;
  style?: React.CSSProperties;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  className = '',
  icon,
  fullWidth = false,
  style
}, ref) => {
  const baseClasses =
    'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium '
    + 'transition-colors focus:outline-none focus-visible:outline focus-visible:outline-2 '
    + 'focus-visible:outline-offset-1 focus-visible:outline-eg-accent '
    + 'disabled:opacity-40 disabled:cursor-not-allowed';

  // L'action engageante est sombre, non colorée : la terre cuite reste
  // réservée à ce qui signale — un état, une alerte, un point d'attention.
  const variantClasses = {
    primary: 'bg-eg-ombre hover:bg-eg-encre text-eg-blanc',
    secondary: 'bg-eg-accent hover:bg-[#2F4A38] text-eg-blanc',
    outline: 'border border-eg-bord bg-eg-blanc hover:bg-eg-papier text-eg-brun',
    ghost: 'text-eg-gris hover:bg-eg-voile hover:text-eg-encre',
    danger: 'bg-[#A33A2B] hover:bg-[#8A2F23] text-eg-blanc'
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-[12.5px]',
    md: 'px-3.5 py-2 text-[13px]',
    lg: 'px-5 py-2.5 text-sm'
  };
  
  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${fullWidth ? 'w-full' : ''} ${className}`;
  
  return (
    <motion.button
      ref={ref}
      type={type}
      className={classes}
      style={style}
      disabled={disabled || loading}
      // Appelé sans argument, conformément au type annoncé. Transmettre
      // l'événement de clic à une fonction qui attend une chaîne facultative
      // ne se voit pas à la compilation, et a coûté le bouton « Envoyer » de
      // l'assistant : il levait au clic, en silence, tout en marchant par la
      // touche Entrée.
      onClick={() => onClick?.()}
    >
      {loading && (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      {icon && !loading && <span className="mr-2">{icon}</span>}
      {children}
    </motion.button>
  );
});

Button.displayName = 'Button';

export default Button;
